import { z } from "zod";
import {
  attendanceMarkSchema,
  attendanceMethodSchema,
  balancePaymentStatusSchema,
  businessRoleSchema,
  teamRoleSchema,
  classKindSchema,
  classLevelSchema,
  groupLinkPurposeSchema,
  groupLinkStatusSchema,
  importClassificationSchema,
  importSourceSchema,
  jobTitleSchema,
  manualPaymentMethodSchema,
  overridableBookingCodes,
  ownerOverrideSchema,
  packageFreezeReasonSchema,
  paymentRequestStatusSchema,
  placementStatusSchema,
  setupStepKeySchema,
  staffRequestWhatsappStatusSchema,
  teamInvitationStatusSchema,
} from "../enums.ts";
import { FREEZE_MAX_DAYS, FREEZE_MIN_DAYS } from "../package-freeze.ts";
import { sessionsLeftSchema } from "../sessions-left.ts";
import { membershipSummarySchema } from "./membership.ts";
import { packageProductSchema } from "./catalog.ts";
import { feeFilsSchema, filsSchema, isoDateSchema, isoDateTimeSchema, localTimeSchema, uuidSchema } from "./common.ts";

/**
 * Business setup and the owner, staff and member surfaces from the mobile app V1 spec. Everything here
 * is a view the app renders as-is; the underlying tables are the ones in `catalog`, `identity` and
 * `billing`.
 */

// --- setup progress --------------------------------------------------------

export const setupStepSchema = z.object({
  key: setupStepKeySchema,
  title: z.string(),
  description: z.string(),
  done: z.boolean(),
  completedAt: isoDateTimeSchema.nullable(),
  /** False when an earlier step gates this one. `gateReason` says why, for the tap. */
  available: z.boolean(),
  gateReason: z.string().nullable(),
});
export type SetupStep = z.infer<typeof setupStepSchema>;

export const setupProgressSchema = z.object({
  steps: z.array(setupStepSchema),
  done: z.number().int(),
  total: z.number().int(),
  nextStep: setupStepKeySchema.nullable(),
  counts: z.object({
    membersImported: z.number().int(),
    membersJoined: z.number().int(),
    teamSize: z.number().int(),
    participants: z.number().int(),
    unplaced: z.number().int(),
    /** Participants a member added that the owner has not confirmed. */
    pendingParticipants: z.number().int(),
    /** Members who joined with the code without being on the list. */
    pendingMembers: z.number().int(),
    unreadThreads: z.number().int(),
    paymentsDue: z.number().int(),
  }),
});
export type SetupProgress = z.infer<typeof setupProgressSchema>;

// --- team ------------------------------------------------------------------

/** The last personal invitation of a team member (migration 0054). */
export const teamInvitationSchema = z.object({
  status: teamInvitationStatusSchema,
  /** What WhatsApp answered. Not `sent`: the message did not go, and the owner can copy the link. */
  whatsappStatus: staffRequestWhatsappStatusSchema,
  sentAt: isoDateTimeSchema,
  revokedAt: isoDateTimeSchema.nullable(),
  /** The personal link, for Copy link. It gives no access alone: the person signs in with a code. */
  link: z.string(),
});
export type TeamInvitation = z.infer<typeof teamInvitationSchema>;

export const teamMemberSchema = z.object({
  /** A business_members id when the person has signed in, else the imported contact id. */
  id: uuidSchema,
  userId: uuidSchema.nullable(),
  name: z.string(),
  /** Null for a person who signs in by email only. `phoneMasked` then says so. */
  phoneE164: z.string().nullable(),
  phoneMasked: z.string(),
  role: businessRoleSchema,
  /** `invited` means they are on the list but have not opened the app yet. */
  status: z.enum(["active", "invited"]),
  isSelf: z.boolean(),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Null for an invited contact. Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
  /** The role at the business as the person said it (owner sign-up). A label only. Optional for older API builds. */
  jobTitle: jobTitleSchema.nullable().optional(),
  /** The last personal invitation. Null when none was sent. Optional for older API builds. */
  invitation: teamInvitationSchema.nullable().optional(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamResponseSchema = z.object({ items: z.array(teamMemberSchema) });

export const addTeamMemberRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Anything a person would type; the API normalises UAE numbers to E.164. */
  phoneE164: z.string().min(6).max(32),
  role: teamRoleSchema,
});
export type AddTeamMemberRequest = z.infer<typeof addTeamMemberRequestSchema>;

export const updateTeamMemberRequestSchema = z.object({ role: teamRoleSchema });

// --- WhatsApp group import -------------------------------------------------

export const groupLinkSchema = z.object({
  id: uuidSchema,
  purpose: groupLinkPurposeSchema,
  status: groupLinkStatusSchema,
  groupName: z.string().nullable(),
  memberCount: z.number().int().nullable(),
  connectedAt: isoDateTimeSchema.nullable(),
  /** The number the owner adds to the group. */
  teamnaNumber: z.string(),
  /**
   * The Teamna number stays in the group after the sync. True for every link opened since
   * 2026-10-09; false only on an older link that read once and left, or after "Remove Teamna".
   */
  stay: z.boolean().optional(),
  /** True when this API cannot read WhatsApp groups and offers a simulated connection instead. */
  simulated: z.boolean(),
  /** True when adding the number really reads the group (Whapi). The link turns connected by itself. */
  live: z.boolean(),
  /**
   * A waiting link: when groups stop being matched to it (two hours after it was opened or
   * reopened). The screen goes back to the start button then. Null when the link is not waiting.
   */
  waitingUntil: isoDateTimeSchema.nullable().optional(),
  /**
   * A waiting link where the owner pasted a group invite link and Teamna waits to be in the group
   * (most likely a group admin must approve it). Never the link or its code.
   */
  invitePending: z.boolean().optional(),
});
export type GroupLink = z.infer<typeof groupLinkSchema>;

/** `live`: Teamna reads the group. `simulated`: a demo list, never on production. `off`: pasted lists and CSV only. */
export const groupImportModeSchema = z.enum(["live", "simulated", "off"]);
export type GroupImportMode = z.infer<typeof groupImportModeSchema>;

export const groupImportOptionsSchema = z.object({
  mode: groupImportModeSchema,
  /** The number the owner adds to the group. */
  teamnaNumber: z.string(),
  /**
   * The proved numbers of the business's owners and admins, masked. The person who adds the Teamna
   * number must be a group admin on one of these, or the group is not matched.
   */
  adminPhonesMasked: z.array(z.string()),
  /** The same numbers in full (E.164), for the owner's own screens. */
  adminPhones: z.array(z.string()),
});
export type GroupImportOptions = z.infer<typeof groupImportOptionsSchema>;

/**
 * The Teamna number always stays in the group until the owner removes it (owner decision
 * 2026-10-09). `stay` is ignored: it is accepted only so an older app that still sends it passes.
 */
export const createGroupLinkRequestSchema = z.object({
  purpose: groupLinkPurposeSchema,
  /** Ignored. The number always stays. */
  stay: z.boolean().optional(),
  /** From the WhatsApp sync tool: new people land in Unsorted. Without it they land as members. */
  sortLater: z.boolean().optional(),
});

export const importedContactSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  phoneE164: z.string(),
  phoneMasked: z.string(),
  classification: importClassificationSchema,
  teamRole: teamRoleSchema.nullable(),
  source: importSourceSchema,
  /** Set once the person signed in with this number. */
  matchedUserId: uuidSchema.nullable(),
  invitedAt: isoDateTimeSchema.nullable(),
});
export type ImportedContact = z.infer<typeof importedContactSchema>;

export const importContactsRequestSchema = z.object({
  source: importSourceSchema,
  purpose: groupLinkPurposeSchema.default("members"),
  groupLinkId: uuidSchema.optional(),
  contacts: z
    .array(
      z.object({
        displayName: z.string().min(1).max(120),
        /** Anything a person would type. The API normalises to E.164 and drops what it cannot read. */
        phone: z.string().min(6).max(32),
      }),
    )
    .min(1)
    .max(2000),
});
export type ImportContactsRequest = z.infer<typeof importContactsRequestSchema>;

export const importReviewSchema = z.object({
  items: z.array(importedContactSchema),
  counts: z.object({
    members: z.number().int(),
    team: z.number().int(),
    skipped: z.number().int(),
    self: z.number().int(),
    /** Lines the API could not read a phone number from. */
    unreadable: z.number().int(),
  }),
});
export type ImportReview = z.infer<typeof importReviewSchema>;

export const updateImportedContactRequestSchema = z.object({
  classification: importClassificationSchema.optional(),
  teamRole: teamRoleSchema.nullable().optional(),
  displayName: z.string().trim().min(1).max(120).optional(),
  /** A corrected number, as a person types it. Refused for a person who already joined with theirs. */
  phone: z.string().trim().min(6).max(32).optional(),
});

// --- CSV import (owner web office) -----------------------------------------

/**
 * One row of a member spreadsheet, after the web app read the file and the owner mapped its
 * columns. The client parses the CSV; the API only sees rows. Empty optional cells are left out.
 */
export const importCsvRowSchema = z.object({
  memberName: z.string().trim().min(1).max(120),
  /** Anything a person would type. The API normalises UAE numbers to E.164 and refuses the rest. */
  phone: z.string().trim().min(6).max(32),
  participantName: z.string().trim().min(1).max(120),
  /**
   * A group name as the business wrote it. Matched without case. Left out means not placed. A name
   * no group has yet waits on the participant until a group with that name is made.
   */
  groupName: z.string().trim().max(120).optional(),
  /** Sessions left this period. Left out means no starting balance for this row. */
  sessionsLeft: z.number().int().min(0).max(200).optional(),
  /** Paid for this period, or owed. Left out means paid. */
  paid: z.boolean().optional(),
  /**
   * The client's email (a client sheet, O1). Kept on an account that nobody signed in to yet and
   * that has no email, when no other account has it. It signs in only after a code proves it.
   */
  email: z.string().trim().toLowerCase().email().max(254).optional(),
  /** The package that holds the sessions left, by name, matched without case. Left out means the first package. */
  packageName: z.string().trim().min(1).max(120).optional(),
  /**
   * Where clients book for themselves (O1): the last day of the package, today or later. Left out
   * means the end of this period. A family business never sends it.
   */
  endsOn: isoDateSchema.optional(),
});
export type ImportCsvRow = z.infer<typeof importCsvRowSchema>;

export const importCsvRequestSchema = z.object({
  rows: z.array(importCsvRowSchema).min(1).max(2000),
});
export type ImportCsvRequest = z.infer<typeof importCsvRequestSchema>;

export const importCsvResponseSchema = z.object({
  rows: z.number().int(),
  /** Accounts made for numbers Teamna did not know yet. The person signs in to the same account. */
  membersCreated: z.number().int(),
  participantsCreated: z.number().int(),
  /** Existing participants whose group changed. */
  participantsUpdated: z.number().int(),
  /** Starting balances written or corrected. */
  balancesSet: z.number().int(),
  /** Rows whose group does not exist yet. The participant goes into it when a group with that name is made. */
  groupsWaiting: z.number().int(),
  /** Rows with sessions left and no package yet. The balance is written when the first package is saved. */
  balancesWaiting: z.number().int(),
  /** Rows that matched what was there already, so nothing changed. */
  unchanged: z.number().int(),
});
export type ImportCsvResponse = z.infer<typeof importCsvResponseSchema>;

// --- timetable (groups and weekly slots) -----------------------------------

export const timetableSlotSchema = z.object({
  id: uuidSchema,
  weekday: z.number().int().min(0).max(6),
  startTime: localTimeSchema,
  durationMin: z.number().int(),
  capacity: z.number().int().nullable(),
  staffUserId: uuidSchema.nullable(),
  /** The session's own name ("Core awake") and what it is ("Mixed apparatus"). Null: the class name. */
  name: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
});

export const timetableGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: classKindSchema,
  color: z.string().nullable(),
  colorIndex: z.number().int(),
  durationMin: z.number().int(),
  capacity: z.number().int().nullable(),
  /** The level's only coach. Null when it has none or more than one. */
  staffUserId: uuidSchema.nullable(),
  staffName: z.string().nullable(),
  /** Every coach of the level, in the owner's order, with their names. */
  staffUserIds: z.array(uuidSchema),
  staffNames: z.array(z.string()),
  /** Open to all levels, with a package of its own (migration 0062). Nobody is placed in it. */
  openToAll: z.boolean().default(false),
  /** The level badge of the class (migration 0074). Null: none. Optional for older API builds. */
  level: classLevelSchema.nullable().optional(),
  sortOrder: z.number().int(),
  participantCount: z.number().int(),
  slots: z.array(timetableSlotSchema),
});
export type TimetableGroup = z.infer<typeof timetableGroupSchema>;

export const timetableSchema = z.object({
  groups: z.array(timetableGroupSchema),
  /** "3 groups · 18 sessions a week · Sat to Thu" */
  summary: z.string(),
  daysOff: z.array(z.number().int()),
});
export type Timetable = z.infer<typeof timetableSchema>;

/**
 * `PUT /business/businesses/:id/groups/:groupId/slots`: one group's weekly slots, replaced as a set.
 * A row with the id of a kept slot updates it; a row without an id is a new slot; a slot that is
 * not in the list stops. Only future sessions change.
 */
export const saveGroupSlotsRequestSchema = z.object({
  slots: z
    .array(
      z.object({
        id: uuidSchema.nullable().optional(),
        weekday: z.number().int().min(0).max(6),
        startTime: localTimeSchema,
        durationMin: z.number().int().min(5).max(600),
        capacity: z.number().int().min(1).max(500).nullable(),
        staffUserId: uuidSchema.nullable().optional(),
      }),
    )
    .max(60),
});
export type SaveGroupSlotsRequest = z.infer<typeof saveGroupSlotsRequestSchema>;

/**
 * One review card: a group (or a private slot type) with a start time and the weekdays it runs on.
 * Two cards with the same name are two time slots of the same group.
 */
export const timetableCardSchema = z.object({
  groupId: uuidSchema.nullable().optional(),
  name: z.string().min(1).max(120),
  kind: classKindSchema.default("group"),
  startTime: localTimeSchema,
  durationMin: z.number().int().min(5).max(600),
  capacity: z.number().int().min(1).max(500).nullable(),
  weekdays: z.array(z.number().int().min(0).max(6)).min(1),
  staffUserId: uuidSchema.nullable().optional(),
  colorIndex: z.number().int().min(0).max(11).optional(),
  /** The level of the class (Studio S2). Left out: a new class has none, a saved class keeps its own. */
  level: classLevelSchema.nullable().optional(),
});
export type TimetableCard = z.infer<typeof timetableCardSchema>;

export const saveTimetableRequestSchema = z.object({
  cards: z.array(timetableCardSchema).min(1).max(60),
});
export type SaveTimetableRequest = z.infer<typeof saveTimetableRequestSchema>;

export const timetableFileSchema = z.object({
  /** Base64 of a JPEG, PNG or PDF. Not stored: the API answers and forgets it. */
  data: z.string().min(16),
  mimeType: z.string().min(3).max(64),
});

/** The most files one read takes: the pages of one timetable. */
export const MAX_TIMETABLE_FILES = 6;
/**
 * The largest extract request body. The reader (Anthropic) refuses a request over 32 MB, and a
 * refused read looks like "not connected" to the owner, so the API stops a larger body first.
 */
export const TIMETABLE_REQUEST_MAX_BYTES = 30 * 1024 * 1024;
/** The most bytes of files the page sends in one read. Base64 makes them a third larger. */
export const TIMETABLE_FILES_MAX_BYTES = 22 * 1024 * 1024;

/**
 * One file (`data` and `mimeType`, what the mobile app sends) or several (`files`: the pages or
 * screenshots of one timetable, read together into one answer).
 */
export const extractTimetableRequestSchema = z
  .object({
    data: timetableFileSchema.shape.data.optional(),
    mimeType: timetableFileSchema.shape.mimeType.optional(),
    files: z.array(timetableFileSchema).min(1).max(MAX_TIMETABLE_FILES).optional(),
  })
  .refine((body) => body.files !== undefined || (body.data !== undefined && body.mimeType !== undefined), {
    message: "Send one file (data and mimeType) or several (files).",
  });
export type ExtractTimetableRequest = z.infer<typeof extractTimetableRequestSchema>;
export type TimetableFile = z.infer<typeof timetableFileSchema>;

export const extractedGroupSchema = z.object({
  name: z.string(),
  startTime: localTimeSchema,
  durationMin: z.number().int(),
  weekdays: z.array(z.number().int().min(0).max(6)),
  capacity: z.number().int().nullable(),
  /** Under 0.7 the review screen highlights the field. */
  confidence: z.number().min(0).max(1),
});

export const extractTimetableResponseSchema = z.object({
  /** True when no reader is configured and the answer is the sport's template instead. */
  simulated: z.boolean(),
  groups: z.array(extractedGroupSchema),
  summary: z.string(),
});
export type ExtractTimetableResponse = z.infer<typeof extractTimetableResponseSchema>;

// --- packages and rules ----------------------------------------------------

export const businessRulesSchema = z.object({
  freeCancelCutoffHours: z.number().int().min(0).max(720),
  lateCancelRule: z.enum(["session_lost", "session_returned"]),
  rescheduleLimitPerMonth: z.number().int().min(0).max(200).nullable(),
  /**
   * The no-show and late-cancel fee in fils (I2, migration 0078). Null: no fee, the start value.
   * Optional: a save without them (the setup step) leaves the fees as they are.
   */
  noShowFeeFils: feeFilsSchema.nullable().optional(),
  lateCancelFeeFils: feeFilsSchema.nullable().optional(),
});
export type BusinessRules = z.infer<typeof businessRulesSchema>;

export const packageCardSchema = z.object({
  id: uuidSchema.nullable().optional(),
  name: z.string().min(1).max(120),
  kind: classKindSchema,
  sessionsCount: z.number().int().min(1).max(1000),
  priceFils: filsSchema.min(0),
  validityDays: z.number().int().min(1).max(730),
  autoRenew: z.boolean(),
  /**
   * An intro offer (migration 0070). Optional: a save without it leaves the package as it is, and a
   * new card is for everyone. The Packages page sets it.
   */
  newClientsOnly: z.boolean().optional(),
});
export type PackageCard = z.infer<typeof packageCardSchema>;

export const savePackagesRequestSchema = z.object({
  packages: z.array(packageCardSchema).min(1).max(30),
  rules: businessRulesSchema,
});
export type SavePackagesRequest = z.infer<typeof savePackagesRequestSchema>;

export const packagesAndRulesSchema = z.object({
  /** `groupIds`: the groups the package covers, null for every group. Record a payment offers a child the packages of their group first. */
  packages: z.array(packageCardSchema.extend({ id: uuidSchema, active: z.boolean(), groupIds: z.array(uuidSchema).nullable().optional() })),
  rules: businessRulesSchema,
  /**
   * The monthly memberships on sale (M2). They are not cards: the Packages page sets them up, and
   * a save of the cards keeps them. The desk sale lists them. Optional for older API builds.
   */
  memberships: z.array(packageProductSchema).optional(),
});
export type PackagesAndRules = z.infer<typeof packagesAndRulesSchema>;

// --- invites ---------------------------------------------------------------

export const invitePreviewSchema = z.object({
  code: z.string(),
  url: z.string(),
  ownerName: z.string(),
  messageBody: z.string(),
  membersToInvite: z.number().int(),
  teamToInvite: z.number().int(),
  /** True when no WhatsApp provider is configured, so sends are recorded but nothing leaves the API. */
  simulated: z.boolean(),
});
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

export const sendInviteRequestSchema = z.object({
  messageBody: z.string().min(10).max(2000),
  dmEach: z.boolean().default(true),
  nudge: z.boolean().default(true),
});

export const inviteStatusSchema = z.object({
  code: z.string(),
  url: z.string(),
  sentAt: isoDateTimeSchema.nullable(),
  invited: z.number().int(),
  joined: z.number().int(),
  notJoined: z.array(
    z.object({
      id: uuidSchema,
      displayName: z.string(),
      /** In full: the owner invited this number. */
      phoneE164: z.string(),
      phoneMasked: z.string(),
      nudgedAt: isoDateTimeSchema.nullable(),
    }),
  ),
  simulated: z.boolean(),
});
export type InviteStatus = z.infer<typeof inviteStatusSchema>;

export const sendResultSchema = z.object({
  sent: z.number().int(),
  simulated: z.boolean(),
});

// --- participants (owner view) ----------------------------------------------

export const participantMemberSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  phoneMasked: z.string(),
  membershipStatus: z.string().nullable(),
});

export const ownerParticipantSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  birthYear: z.number().int().nullable(),
  age: z.number().int().nullable(),
  members: z.array(participantMemberSchema),
  groupId: uuidSchema.nullable(),
  groupName: z.string().nullable(),
  /** What the member picked when they added the participant, before any owner change. */
  memberPickedGroupId: uuidSchema.nullable(),
  usualWeekdays: z.array(z.number().int()),
  placementStatus: placementStatusSchema,
  confirmedAt: isoDateTimeSchema.nullable(),
  createdBy: z.enum(["member", "owner"]),
  photoUrl: z.string().nullable(),
  /** The family said no profile photo (migration 0029). Optional for older fixtures; missing reads as false. */
  photosRefused: z.boolean().optional(),
  sessionsLeft: z.number().int(),
  packageName: z.string().nullable(),
  paymentStatus: balancePaymentStatusSchema.nullable(),
  renewsOn: isoDateSchema.nullable(),
  /** Only a membership month with no limit pays (no package): there is no count. Optional for older API builds. */
  unlimited: z.boolean().optional(),
  /**
   * The bar of the attendance register for this child's class today (`sessionsLeftView`). Null with
   * no class. Optional for older API builds.
   */
  sessionsLeftFacts: sessionsLeftSchema.nullable().optional(),
  staffName: z.string().nullable(),
  notes: z.string().nullable(),
  /** The newest booking or balance line for this participant. Optional for older API builds. */
  lastActivityAt: isoDateTimeSchema.nullable().optional(),
  /**
   * False when this business does not hold the row: the person's own row that belongs to another
   * business or to none (D36). The business does not place or confirm it here (D26). Optional for
   * older API builds (then true).
   */
  heldHere: z.boolean().optional(),
});
export type OwnerParticipant = z.infer<typeof ownerParticipantSchema>;

export const participantsResponseSchema = z.object({ items: z.array(ownerParticipantSchema) });

export const placeParticipantRequestSchema = z.object({
  groupId: uuidSchema.nullable().optional(),
  placementStatus: placementStatusSchema.optional(),
  usualWeekdays: z.array(z.number().int().min(0).max(6)).optional(),
  notes: z.string().max(1000).nullable().optional(),
  confirm: z.boolean().optional(),
});
export type PlaceParticipantRequest = z.infer<typeof placeParticipantRequestSchema>;

export const createParticipantByOwnerRequestSchema = z.object({
  memberUserId: uuidSchema,
  displayName: z.string().min(1).max(120),
  birthYear: z.number().int().min(1900).max(2100).nullable().optional(),
  groupId: uuidSchema.nullable().optional(),
  usualWeekdays: z.array(z.number().int().min(0).max(6)).optional(),
});

// --- starting balances -----------------------------------------------------

export const startingBalanceParticipantSchema = z.object({
  /**
   * The participant. Null only where clients book for themselves (O1), for a client with no profile
   * at the business yet: the save then names the client (`memberUserId`) and makes the profile.
   */
  id: uuidSchema.nullable(),
  displayName: z.string(),
  usualWeekdays: z.array(z.number().int()),
  groupName: z.string().nullable(),
  packageId: uuidSchema.nullable(),
  packageName: z.string().nullable(),
  /** Occurrences of the usual weekdays between today and the period end. */
  defaultSessions: z.number().int(),
  /** Already saved for this period, if any. */
  currentSessions: z.number().int().nullable(),
  /**
   * Where clients book for themselves (O1): the last day of the saved package, in the business's
   * time zone. Null when nothing is saved. Left out by a family business, whose packages end with
   * the period.
   */
  endsOn: isoDateSchema.nullable().optional(),
});

export const startingBalanceFamilySchema = z.object({
  userId: uuidSchema,
  memberName: z.string(),
  /** In full, for the owner's setup step. Null for a parent who signs in by email only. */
  phoneE164: z.string().nullable(),
  phoneMasked: z.string(),
  paid: z.boolean(),
  participants: z.array(startingBalanceParticipantSchema),
});

export const startingBalancesSchema = z.object({
  periodEnd: isoDateSchema,
  renewsOn: isoDateSchema,
  savedAt: isoDateTimeSchema.nullable(),
  packages: z.array(z.object({ id: uuidSchema, name: z.string(), kind: classKindSchema, sessionsCount: z.number().int() })),
  families: z.array(startingBalanceFamilySchema),
  totals: z.object({ sessions: z.number().int(), familiesPaid: z.number().int(), familiesOwing: z.number().int() }),
});
export type StartingBalances = z.infer<typeof startingBalancesSchema>;

export const saveStartingBalancesRequestSchema = z.object({
  entries: z
    .array(
      z.object({
        /** The participant. A family business always sends it. */
        participantId: uuidSchema.optional(),
        /**
         * Where clients book for themselves (O1), in place of `participantId` for a client with no
         * profile here yet: the family id of the row. The API makes the client's own profile.
         */
        memberUserId: uuidSchema.optional(),
        packageId: uuidSchema,
        sessions: z.number().int().min(0).max(200),
        paid: z.boolean(),
        /**
         * Where clients book for themselves (O1): the last day of the package, today or later. Left
         * out means the end of this period. A family business never sends it.
         */
        endsOn: isoDateSchema.optional(),
      }),
    )
    .min(1),
});
export type SaveStartingBalancesRequest = z.infer<typeof saveStartingBalancesRequestSchema>;

// --- participant profile ----------------------------------------------------

export const ledgerLineSchema = z.object({
  id: uuidSchema,
  createdAt: isoDateTimeSchema,
  delta: z.number().int(),
  reason: z.string(),
  /** The sentence the member reads, reason label included. */
  label: z.string(),
  actorName: z.string().nullable(),
  sessionStartsAt: isoDateTimeSchema.nullable(),
});
export type LedgerLine = z.infer<typeof ledgerLineSchema>;

/** A pause of a package (migration 0071): the days are `yyyy-MM-dd` in the business timezone, the last day included. */
export const packagePauseSchema = z.object({
  freezeId: uuidSchema,
  startsOn: isoDateSchema,
  /** The last paused day. After an early end, the last day that was paused. */
  endsOn: isoDateSchema,
  reason: packageFreezeReasonSchema,
});
export type PackagePause = z.infer<typeof packagePauseSchema>;

export const balanceSummarySchema = z.object({
  purchaseId: uuidSchema.nullable(),
  sessionsLeft: z.number().int(),
  packageName: z.string().nullable(),
  packageKind: classKindSchema.nullable(),
  sessionsCount: z.number().int().nullable(),
  priceFils: filsSchema.nullable(),
  renewsOn: isoDateSchema.nullable(),
  expiresAt: isoDateTimeSchema.nullable(),
  paymentStatus: balancePaymentStatusSchema.nullable(),
  autoRenew: z.boolean().nullable(),
  /** Sessions that expired unused when the last period closed. */
  expiredLastPeriod: z.number().int(),
  /**
   * The pause in force today or the next one to start, on a package of this kind (`openPause`).
   * Optional so older API builds and fixtures written before it keep parsing.
   */
  pause: packagePauseSchema.nullish(),
  /**
   * The package of this kind that a freeze pauses: the newest one that is active, not past its end
   * date, and has sessions left. It can be older than `purchaseId` when the newest is used up.
   * Optional so older API builds and fixtures keep parsing.
   */
  freezable: z.object({ purchaseId: uuidSchema, packageName: z.string().nullable(), expiresAt: isoDateTimeSchema }).nullish(),
  /**
   * The package that waits for the one that runs now, and starts when it ends (migration 0085,
   * #ai-feedback 2026-10-26). Its sessions are not in `sessionsLeft` before its first day.
   */
  nextPackage: z.object({ purchaseId: uuidSchema, packageName: z.string(), startsAt: isoDateTimeSchema, sessions: z.number().int() }).nullish(),
});
export type BalanceSummary = z.infer<typeof balanceSummarySchema>;

export const upcomingBookingSchema = z.object({
  bookingId: uuidSchema,
  sessionId: uuidSchema,
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  kind: classKindSchema,
  staffName: z.string().nullable(),
  /** The engine's own sentence for cancelling now. */
  cancelPreview: z.string(),
  freeUntil: isoDateTimeSchema.nullable(),
});
export type UpcomingBooking = z.infer<typeof upcomingBookingSchema>;

/**
 * One pack of the family on the client page (Pilates studio shape, C1): each pack its own row, with
 * its own pause and its own Freeze. A private pack can be frozen too (plan decision D17).
 */
export const profilePackSchema = z.object({
  purchaseId: uuidSchema,
  packageName: z.string(),
  packageKind: classKindSchema,
  sessionsLeft: z.number().int().min(0),
  sessionsCount: z.number().int(),
  expiresAt: isoDateTimeSchema,
  /** The last day of the pack, in the business timezone. */
  endsOn: isoDateSchema,
  /** `current` can pay for a session (also while paused); `used_up` has no session left; `ended` is past its end date or refunded. */
  state: z.enum(["current", "used_up", "ended"]),
  paymentStatus: balancePaymentStatusSchema,
  /** An intro offer (`newClientsOnly`, M1). */
  intro: z.boolean(),
  /** The pause in force today, else the next one to start. */
  pause: packagePauseSchema.nullable(),
  /** Freeze can pause it: current and with sessions left. A membership month cannot be frozen (M2). */
  canFreeze: z.boolean(),
  /** One month of a monthly membership (M2). Optional for older API builds. */
  membership: z.boolean().optional(),
  /** A month of a membership with no cap: `sessionsLeft` means nothing then. */
  unlimited: z.boolean().optional(),
  /**
   * The participant the pack was bought for, or null when it was bought for the account (D17: the
   * Freeze of one child pauses the packs bought for that child). Optional for older API builds.
   */
  participantId: uuidSchema.nullable().optional(),
  /** The first day of a package that waits for the package before it (card #1). Null: it runs now. */
  startsOn: isoDateSchema.nullable().optional(),
  /** Extend can move its last day: it runs (or waits), and it is not a membership month (card #1). Optional for older API builds. */
  canExtend: z.boolean().optional(),
  /**
   * Carry over can move its unused sessions now: it runs, has sessions left, counts sessions, and a
   * package that counts sessions waits for it (card #1). Optional for older API builds.
   */
  canCarryOver: z.boolean().optional(),
});
export type ProfilePack = z.infer<typeof profilePackSchema>;

/** A session the participant came to (booked, started, not marked absent), newest first. */
export const profileVisitSchema = z.object({
  bookingId: uuidSchema,
  sessionId: uuidSchema,
  startsAt: isoDateTimeSchema,
  groupName: z.string(),
  staffName: z.string().nullable(),
  /** The staff member marked them present. False: booked and not marked absent. */
  marked: z.boolean(),
});
export type ProfileVisit = z.infer<typeof profileVisitSchema>;

/** One package the business recorded as not paid yet: a starting balance, a renewal, or a sale on credit. */
export const owedBalanceSchema = z.object({
  purchaseId: uuidSchema,
  packageName: z.string(),
  /** Still to pay: the price less any part payments. */
  amountFils: filsSchema,
  /** The full price, and what part payments brought in so far. Optional for older API builds. */
  priceFils: filsSchema.optional(),
  paidFils: filsSchema.optional(),
  /** The period it pays for, when it has one. */
  renewsOn: isoDateSchema.nullable(),
  purchasedAt: isoDateTimeSchema,
  /** The link that pays it by card without a sign-in. Optional for older API builds. */
  payLink: z.string().optional(),
});
export type OwedBalance = z.infer<typeof owedBalanceSchema>;

/**
 * An amount the owner asked one member for (kind `one_off`), as the owner reads it on the profile.
 * The member pays it in the app, or with the link, which needs no sign-in.
 */
export const ownerPaymentRequestSchema = z.object({
  id: uuidSchema,
  /** What it is for, in the owner's words. */
  description: z.string(),
  amountFils: filsSchema,
  dueOn: isoDateSchema,
  status: paymentRequestStatusSchema,
  /** Who it is about: a child, or the member. Null when that person was removed. */
  participantId: uuidSchema.nullable(),
  participantName: z.string().nullable(),
  /** The link that pays it. Null when nothing is left to pay (paid or cancelled). */
  payLink: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  paidAt: isoDateTimeSchema.nullable(),
});
export type OwnerPaymentRequest = z.infer<typeof ownerPaymentRequestSchema>;

/** `POST /business/businesses/:id/participants/:participantId/payment-requests`: ask the family for an amount. */
export const createPaymentRequestSchema = z.object({
  /** At least AED 2 (Stripe takes no card payment under AED 2.00) and at most AED 100,000. */
  amountFils: filsSchema.min(200).max(10_000_000),
  /** What it is for. The member reads it on the payment page and on the Stripe page. */
  description: z.string().trim().min(2).max(80),
  /** The day to pay by, in the business timezone. Today when missing. */
  dueOn: isoDateSchema.optional(),
});
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>;

export const memberProfileSchema = z.object({
  participant: ownerParticipantSchema,
  balances: z.array(balanceSummarySchema),
  upcoming: z.array(upcomingBookingSchema),
  ledger: z.array(ledgerLineSchema),
  /** The member's private thread with the business, for the message button. */
  conversationId: uuidSchema.nullable(),
  /** Packages of this participant's family that are not paid yet, oldest first. Optional for older API builds. */
  owed: z.array(owedBalanceSchema).default([]),
  /** Amounts the business asked this family for (kind `one_off`), newest first: open ones and the last paid ones. Optional for older API builds. */
  requests: z.array(ownerPaymentRequestSchema).default([]),
  /** Every pack of the family, current first, then the last ended ones (C1). Optional for older API builds. */
  packs: z.array(profilePackSchema).default([]),
  /** The last sessions the participant came to, newest first (C1). Optional for older API builds. */
  visits: z.array(profileVisitSchema).default([]),
  /** The family's running monthly membership (M2), or null. Optional for older API builds. */
  membership: membershipSummarySchema.nullable().optional(),
});
export type MemberProfile = z.infer<typeof memberProfileSchema>;

/**
 * `POST /business/businesses/:id/participants/:participantId/sell-package`: the owner sells a
 * package at the desk. `method` is how the member paid; null means they pay later, and the package
 * shows as owed until the owner records the payment or the member pays by card.
 */
export const sellPackageRequestSchema = z.object({
  productId: uuidSchema,
  method: manualPaymentMethodSchema.nullable(),
  note: z.string().trim().max(200).optional(),
  /** Less than the price is a part payment: the package stays owed for the rest. Needs a method. */
  amountFils: filsSchema.positive().optional(),
  /** The day the money came in, in the business timezone, up to 7 days back. Today when missing. */
  paidOn: isoDateSchema.optional(),
  /**
   * Sell an intro offer (`newClientsOnly`) to a person who is not a new client. Without it the API
   * refuses with NEW_CLIENTS_ONLY. The audit row of the sale records the override.
   */
  overrideNewClientsOnly: z.boolean().optional(),
  /**
   * Start the package today. Without it a package waits for the package that runs for the same
   * person and kind, and starts when that one ends (#ai-feedback 2026-10-26, card #1).
   */
  startToday: z.boolean().optional(),
  /**
   * A proof of payment uploaded before (`POST payment-proofs`): it goes on the payment this call
   * records. Only with a method: a sale to pay later records no payment.
   */
  proofId: uuidSchema.optional(),
});
export type SellPackageRequest = z.infer<typeof sellPackageRequestSchema>;

/**
 * `GET /business/businesses/:id/participants/:participantId/package-start?productId=`: the days a
 * package sold now would run, for the sell panel. `waitsFor` names the package it waits for, null
 * when it starts today. Days are `yyyy-MM-dd` in the business timezone; both are in the period.
 */
export const packageStartSchema = z.object({
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  waitsFor: z.object({ name: z.string(), endsOn: isoDateSchema }).nullable(),
  /** The days if the owner starts it today instead. */
  today: z.object({ startsOn: isoDateSchema, endsOn: isoDateSchema }),
});
export type PackageStart = z.infer<typeof packageStartSchema>;

/** `POST /business/businesses/:id/participants/:participantId/record-payment`: an owed package was paid in cash or by transfer. */
export const recordPaymentRequestSchema = z.object({
  purchaseId: uuidSchema,
  method: manualPaymentMethodSchema,
  note: z.string().trim().max(200).optional(),
  /** Less than what is due is a part payment: the package stays owed for the rest. Missing pays it all. */
  amountFils: filsSchema.positive().optional(),
  /** The day the money came in, in the business timezone, up to 7 days back. Today when missing. */
  paidOn: isoDateSchema.optional(),
  /**
   * A proof of payment uploaded before (`POST payment-proofs`): it goes on the payment this call
   * records. Only with a method: a sale to pay later records no payment.
   */
  proofId: uuidSchema.optional(),
});
export type RecordPaymentRequest = z.infer<typeof recordPaymentRequestSchema>;

export const adjustSessionsRequestSchema = z.object({
  direction: z.enum(["add", "remove"]),
  amount: z.number().int().min(1).max(12),
  reason: z.string().min(1).max(60),
  note: z.string().max(500).optional(),
  /** Which balance to adjust. Defaults to the participant's active group balance. */
  purchaseId: uuidSchema.optional(),
});
export type AdjustSessionsRequest = z.infer<typeof adjustSessionsRequestSchema>;

/** Pause one package of the family (migration 0071). Owner and admin, and the front desk with Freeze on. */
export const freezePackageRequestSchema = z.object({
  purchaseId: uuidSchema,
  /** The first paused day, today or later, in the business timezone. */
  startsOn: isoDateSchema,
  days: z.number().int().min(FREEZE_MIN_DAYS).max(FREEZE_MAX_DAYS),
  reason: packageFreezeReasonSchema,
});
export type FreezePackageRequest = z.infer<typeof freezePackageRequestSchema>;

/** Extend (card #1): the new last day of one package, later than the last day now. Owner and admin. */
export const extendPackageRequestSchema = z.object({
  purchaseId: uuidSchema,
  /** The new last day, in the business timezone. */
  endsOn: isoDateSchema,
});
export type ExtendPackageRequest = z.infer<typeof extendPackageRequestSchema>;

/** Carry over (card #1): move the unused sessions of one package to the package that waits for it. Owner and admin. */
export const carryOverPackageRequestSchema = z.object({
  purchaseId: uuidSchema,
  /** The unused sessions the owner confirmed. When the package has another number now, nothing moves (409). */
  sessions: z.number().int().min(1),
});
export type CarryOverPackageRequest = z.infer<typeof carryOverPackageRequestSchema>;

/**
 * Which packs a pause of many packs takes (D17): `all` is every pack of the account (the client, or
 * every child of a family), `participant` the packs bought for this one participant (one child).
 */
export const freezeScopes = ["all", "participant"] as const;
export const freezeScopeSchema = z.enum(freezeScopes);
export type FreezeScope = z.infer<typeof freezeScopeSchema>;

/** `POST .../participants/:id/freeze-many`: pause every pack in the scope with the same days and reason. Owner and admin, and the front desk with Freeze on. */
export const freezeManyRequestSchema = freezePackageRequestSchema.omit({ purchaseId: true }).extend({ scope: freezeScopeSchema });
export type FreezeManyRequest = z.infer<typeof freezeManyRequestSchema>;

/** A pack the pause of many packs left as it was, and why, in words a person reads. */
export const freezeSkippedSchema = z.object({ purchaseId: uuidSchema, packageName: z.string(), reason: z.string() });
export type FreezeSkipped = z.infer<typeof freezeSkippedSchema>;

export const freezeManyResponseSchema = z.object({
  profile: memberProfileSchema,
  /** The packs it paused. */
  frozen: z.number().int().min(1),
  skipped: z.array(freezeSkippedSchema),
});
export type FreezeManyResponse = z.infer<typeof freezeManyResponseSchema>;

/** `POST .../participants/:id/freeze-many/end`: end today every pause in force or to come of the packs in the scope. */
export const endManyPausesRequestSchema = z.object({ scope: freezeScopeSchema });
export type EndManyPausesRequest = z.infer<typeof endManyPausesRequestSchema>;

export const endManyPausesResponseSchema = z.object({ profile: memberProfileSchema, ended: z.number().int().min(1) });
export type EndManyPausesResponse = z.infer<typeof endManyPausesResponseSchema>;

export const bookForParticipantRequestSchema = z.object({
  participantId: uuidSchema,
  /**
   * Add anyway (#ai-feedback 2026-10-26): when no package pays for this session, book it with
   * nothing taken, `owed` (the next package pays it) or `free`. The owner and admins always may;
   * other staff only with the club setting `staffCanAddAnyway`. When a package pays, it pays.
   */
  override: ownerOverrideSchema.optional(),
});

export const bookForParticipantResponseSchema = z.object({
  bookingId: uuidSchema,
  sessionsLeft: z.number().int(),
  /** Add anyway: how this booking was made with no package. Null when a package paid. Missing from older API builds. */
  override: ownerOverrideSchema.nullable().optional(),
});
export type BookForParticipantResponse = z.infer<typeof bookForParticipantResponseSchema>;

/**
 * Tell the client (#ai-feedback 2026-10-26): no package pays for this session, so the business sends
 * the client a message in their private thread with the club (with a push). The owner can change
 * the text. `reason` is the refusal the business saw (for the record and the analytics).
 * `payLink`: add the link to buy a package (only where the club takes card payments).
 */
export const tellClientRequestSchema = z.object({
  participantId: uuidSchema,
  text: z.string().trim().min(1).max(1000),
  reason: z.enum(overridableBookingCodes),
  /** With `INSUFFICIENT_CREDITS`: the package has sessions but is not paid yet (`meta.reason: "unpaid"`). */
  unpaid: z.boolean().default(false),
  payLink: z.boolean().default(false),
});
export type TellClientRequest = z.input<typeof tellClientRequestSchema>;

// --- schedule (owner and staff) --------------------------------------------

export const rosterEntrySchema = z.object({
  bookingId: uuidSchema,
  participantId: uuidSchema,
  name: z.string(),
  memberUserId: uuidSchema,
  memberName: z.string().nullable(),
  /** "trial", "make-up session", "first time on a Monday" */
  note: z.string().nullable(),
  /** Who came, as staff marked it. Null until marked. Optional for older API builds. */
  attendance: attendanceMarkSchema.nullable().optional(),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
  /**
   * A swimmer the coach added who came and was not booked (migration 0038): the extra's id, for
   * Remove. Null for a booking made the usual way. Optional for older API builds.
   */
  extraId: uuidSchema.nullable().optional(),
  /**
   * The person answered yes to a health question at this business (C2): the team sees the flag
   * "Health note", never the answers. Optional for older API builds.
   */
  healthNote: z.boolean().optional(),
  /**
   * The person's first class at the business (C3): no earlier booking that was not cancelled and
   * not marked absent (`services/first-class.ts`). Optional for older API builds.
   */
  firstClass: z.boolean().optional(),
  /** When the person was checked in by name (I1, migration 0076). Null for a mark made with the cards or the list. Optional for older API builds. */
  checkedInAt: isoDateTimeSchema.nullable().optional(),
  /** Absent, marked by the job 10 minutes after the start (I1), not by a person. Optional for older API builds. */
  autoAbsent: z.boolean().optional(),
  /**
   * The family's sessions left for this class, for the bar on the register (`sessionsLeftView`). Only
   * the one-session read sends it (the screen that takes attendance). Optional for older API builds.
   */
  sessionsLeft: sessionsLeftSchema.nullable().optional(),
});

export const businessSessionSchema = z.object({
  id: uuidSchema,
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  durationMin: z.number().int(),
  status: z.string(),
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  kind: classKindSchema,
  capacity: z.number().int().nullable(),
  bookedCount: z.number().int(),
  staffUserId: uuidSchema.nullable(),
  staffName: z.string().nullable(),
  venueName: z.string().nullable(),
  /** The single booked participant's name on a private slot. */
  privateParticipantName: z.string().nullable(),
  isMine: z.boolean(),
  roster: z.array(rosterEntrySchema),
  /**
   * Names the coach added who came and wait for a parent (migration 0038). They have no booking
   * yet, so they are not in `roster`; they count as Came. Optional for older API builds.
   */
  waitingExtras: z.array(z.object({ id: uuidSchema, name: z.string() })).optional(),
  /** People on the waiting list of the session (`session_waitlist`, status waiting). Optional for older API builds. */
  waitlistCount: z.number().int().min(0).optional(),
  /**
   * Booked people who come here for the first time: this session is the first confirmed booking
   * they have at the business, by session start (H1). Optional for older API builds.
   */
  firstTimers: z.number().int().min(0).optional(),
  /**
   * Booked people with a health note the team must read before the session (C2): a yes on a
   * health question at this business. Ids only, never the answers. Optional for older API builds.
   */
  healthFlags: z.array(z.object({ participantId: uuidSchema })).optional(),
  /**
   * Check-in by name (I1): when the job marks the clients with no mark absent. Null where the
   * business does not check in by name, or turned it off. Optional for older API builds.
   */
  autoAbsentAt: isoDateTimeSchema.nullable().optional(),
});
export type BusinessSession = z.infer<typeof businessSessionSchema>;

/** The answer to a mark: how many bookings changed, and the session with its roster as it is now. */
export const markAttendanceResponseSchema = z.object({
  changed: z.number().int(),
  session: businessSessionSchema,
});
export type MarkAttendanceResponse = z.infer<typeof markAttendanceResponseSchema>;

export const ownerScheduleResponseSchema = z.object({ items: z.array(businessSessionSchema) });

export const runningLateRequestSchema = z.object({ minutes: z.number().int().min(5).max(120) });

/**
 * The most bookings one attendance save carries: twice the largest session capacity (500), so a
 * full session with walk-ins fits (review F2.2). A session with no capacity and more than 1000
 * bookings could not save attendance; no class or session at an activity business is near that
 * (review F3.1, declined).
 */
export const ATTENDANCE_MAX_ROSTER = 1000;

/**
 * Mark who came. One line per booking; `attendance: null` clears a mark. Marking a booking with the
 * value it already has changes nothing, so a retry is safe. A no-show changes no balance.
 *
 * The attendance screens also say what they read (mobile V2 slice 1.1, internal review): `expected`
 * is the mark the screen showed for that booking, and `roster` is every confirmed booking it showed.
 * When either changed on another device since, the save is refused (ATTENDANCE_CHANGED), so a stale
 * screen never writes over a newer mark or saves a register without a new booking. Both are
 * optional, for older app builds that mark one row at a time.
 */
export const markAttendanceRequestSchema = z.object({
  marks: z
    .array(
      z.object({
        bookingId: uuidSchema,
        attendance: attendanceMarkSchema.nullable(),
        expected: attendanceMarkSchema.nullable().optional(),
      }),
    )
    .min(1)
    .max(ATTENDANCE_MAX_ROSTER),
  roster: z.array(uuidSchema).max(ATTENDANCE_MAX_ROSTER).optional(),
  /**
   * How the person marked (I1): `checkin` (one tap, saved at once; a present mark gets
   * `checked_in_at`), `headcount` (the cards) or `list`. Optional for older app builds: the API then
   * reads the person's chosen way (`users.attendance_mode`).
   */
  method: attendanceMethodSchema.optional(),
});
export type MarkAttendanceRequest = z.infer<typeof markAttendanceRequestSchema>;
export const updateSessionStaffRequestSchema = z.object({ staffUserId: uuidSchema.nullable() });

export const staffTodaySchema = z.object({
  mine: z.array(businessSessionSchema),
  others: z.array(businessSessionSchema),
  unreadThreads: z.number().int(),
  latestThread: z
    .object({ conversationId: uuidSchema, memberName: z.string(), preview: z.string(), at: isoDateTimeSchema })
    .nullable(),
});
export type StaffToday = z.infer<typeof staffTodaySchema>;

// --- member home -----------------------------------------------------------

export const memberParticipantSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
  /** The group the business placed the participant in, so the home can find its open usual days. */
  groupId: uuidSchema.nullable(),
  groupName: z.string().nullable(),
  /** The group's colour, for the participant's dots and rails on the member home. */
  groupColor: z.string().nullable(),
  /**
   * The sessions a booking in the participant's own group can spend (the booking's package rule:
   * kind, group list, validity, paid), so one-tap booking never offers more than will go through.
   * Null without a group. Optional so older clients keep parsing.
   */
  groupSessionsLeft: z.number().int().nullable().optional(),
  /**
   * The packages behind `groupSessionsLeft`, in the order the booking picks from. A family package
   * (no participant) is listed on every child with the same id, so the home spends it once.
   */
  groupPurchases: z
    .array(z.object({ purchaseId: uuidSchema, balance: z.number().int(), expiresAt: isoDateTimeSchema }))
    .nullable()
    .optional(),
  placementStatus: placementStatusSchema,
  confirmedAt: isoDateTimeSchema.nullable(),
  usualWeekdays: z.array(z.number().int()),
  balances: z.array(balanceSummarySchema),
  sessionsLeft: z.number().int(),
  nextSession: z
    .object({ bookingId: uuidSchema, sessionId: uuidSchema, startsAt: isoDateTimeSchema, groupName: z.string(), staffName: z.string().nullable() })
    .nullable(),
  /** The starting number the owner set, so the member can flag a mistake. */
  startingBalance: z
    .object({ sessions: z.number().int(), setAt: isoDateTimeSchema, periodEnd: isoDateSchema.nullable() })
    .nullable(),
});
export type MemberParticipant = z.infer<typeof memberParticipantSchema>;

export const memberHomeSchema = z.object({
  business: z.object({
    id: uuidSchema,
    name: z.string(),
    ownerName: z.string().nullable(),
    membershipStatus: z.string(),
    venueName: z.string().nullable(),
  }),
  participants: z.array(memberParticipantSchema),
  paymentDue: z
    .object({
      /** The owed purchase, for `POST /v1/purchases/:id/checkout`. Optional so older clients keep parsing. */
      purchaseId: uuidSchema.optional(),
      amountFils: filsSchema,
      dueOn: isoDateSchema,
      packageName: z.string(),
      participantName: z.string(),
    })
    .nullable(),
  unreadThread: z.number().int(),
  unreadAnnouncements: z.number().int(),
  groups: z.array(z.object({ id: uuidSchema, name: z.string(), kind: classKindSchema })),
  activeWeekdays: z.array(z.number().int()),
  rules: businessRulesSchema,
  /** The account's running monthly membership (M2), or null. Optional for older API builds. */
  membership: membershipSummarySchema.nullable().optional(),
});
export type MemberHome = z.infer<typeof memberHomeSchema>;

export const ledgerResponseSchema = z.object({ items: z.array(ledgerLineSchema) });

export const flagBalanceRequestSchema = z.object({
  participantId: uuidSchema,
  message: z.string().min(1).max(1000),
});
export type FlagBalanceRequest = z.infer<typeof flagBalanceRequestSchema>;
